<template>
	<div class="c-ir-press" v-if="pressReleases.length">
		<ul class="c-ir-press__list">
			<li
				class="c-ir-press__item"
				v-for="press in pressReleases"
				:key="press.id"
			>
				<time class="c-ir-press__date" :datetime="press.date"
					><DsText
						text-type="body-xs"
						text-style="regular"
						html-element="span"
						>{{ formatDate(press.date) }}</DsText
					></time
				>
				<a
					class="c-ir-press__link"
					:href="decodeURIComponent(press.link)"
					@click.prevent="openPopup(press.link)"
					target="_blank"
				>
					<DsText
						text-type="body-lg"
						text-style="medium"
						html-element="span"
						><span v-html="press.title"></span></DsText
					>
				</a>
			</li>
		</ul>
	</div>
</template>

<script lang="ts" setup>
import { ref, onMounted } from 'vue';
import { DsText } from '@coloplast/design-system';

const PRESS_API = `https://tools.eurolandir.com/tools/services/?func=GetCompanyPressReleases&companycode=DK-COLO&lang=en-gb&format=json&amount=3`;
const pressReleases = ref<
	{ id: string; title: string; link: string; date: string }[]
>([]);

let popupOpened = false;

const formatDate = (dateString: string) =>
	new Intl.DateTimeFormat('en-GB', {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	}).format(new Date(dateString));

const openPopup = (url: string) => {
	if (popupOpened) return;
	popupOpened = true;

	const popupFeatures = `width=800,height=1000,menubar=0,toolbar=0,location=0,scrollbars=1,resizable=1`;
	window.open(decodeURIComponent(url), '_blank', popupFeatures);

	setTimeout(() => {
		popupOpened = false;
	}, 1000);
};

onMounted(async () => {
	try {
		const response = await fetch(PRESS_API);
		if (!response.ok) throw new Error('Failed to fetch press releases');
		const data = await response.json();

		// Ensure the data is an array before assigning
		pressReleases.value = Array.isArray(data) ? data : [];
	} catch (error) {
		console.error('Error fetching press releases:', error);
	}
});
</script>

<style lang="scss" scoped>
@import 'multisite/styles/settings/settings';
@import 'multisite/styles/tools/tools';

.c-ir-press {
	--link-cols: span 2;
	--date-cols: span 2;

	@media (min-width: ds-breakpoint('sm')) {
		--link-cols: span 12;
		--date-cols: span 12;
	}

	@media (min-width: ds-breakpoint('lg')) {
		--link-cols: span 10;
		--date-cols: span 2;
	}

	&__list {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	&__link {
		grid-column: var(--link-cols);
		text-decoration: none;
		text-wrap: balance;

		&:hover {
			text-decoration: underline;
		}

		@include ds-focus();
	}

	&__item {
		@include ds-block-grid();
		border-block-end: 1px solid ds-color('border', 'muted');
		padding-block: ds-space(8);
		row-gap: ds-gap(4);
	}

	&__date {
		color: ds-color(fg, muted);
		grid-column: var(--date-cols);
	}
}
</style>
